import React, { useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { formatApiError } from "../lib/format";

export default function Imports() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/imports/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "dwgos_import_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onSelect = (e) => {
    setPreview(null); setDone(null);
    setFile(e.target.files?.[0] || null);
  };

  const runPreview = async () => {
    if (!file) return toast.error("Pilih file .xlsx dulu");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/imports/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success("Pratinjau siap. Cek jumlah baris & error sebelum import.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runExecute = async () => {
    if (!file) return;
    if (!window.confirm("Yakin import? Data yang cocok akan di-MERGE (tidak menimpa), data baru akan dibuat. Paket dari Master Pricelist akan auto-sync ke divisi terkait.")) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/imports/execute", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data);
      const s = data.summary;
      toast.success(`Import: ${s.bookings_new || 0} booking baru, ${s.bookings_merged || 0} merged, ${s.services_auto_added || 0} divisi auto-sync.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const errCount = preview?.errors?.length || 0;
  const warnCount = preview?.warnings?.length || 0;

  return (
    <div className="space-y-6" data-testid="imports-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-[#1E2022]">Import Data dari Excel</h1>
        <p className="text-sm text-[#5C6066] mt-1 max-w-2xl">
          Migrasi data konvensional (spreadsheet lama) ke DWG OS dalam sekali unggah — Klien &amp; Booking,
          Pembayaran / DP, Piutang, hingga Gaji karyawan. Semua baris dibuat sebagai data baru; sistem tidak
          akan menimpa data yang sudah ada.
        </p>
      </div>

      <Card className="p-5 border border-[#E5E0D8] bg-gradient-to-br from-[#FAF6EE] to-white shadow-none">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-11 h-11 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
              <FileSpreadsheet className="w-5 h-5 text-brand"/>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">Langkah 1</div>
              <h3 className="font-display text-base font-bold text-[#1E2022]">Unduh Template Excel Resmi</h3>
              <p className="text-xs text-[#5C6066] mt-0.5 max-w-lg">Template berisi 4 sheet siap isi + petunjuk. Isi kolom "booking_ref" sebagai kunci penghubung antar sheet.</p>
            </div>
          </div>
          <Button onClick={downloadTemplate} className="bg-brand hover:bg-brand/90 text-white" data-testid="btn-download-template">
            <Download className="w-4 h-4 mr-1.5"/> Unduh Template
          </Button>
        </div>
      </Card>

      <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
            <Upload className="w-5 h-5 text-brand"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">Langkah 2</div>
            <h3 className="font-display text-base font-bold text-[#1E2022]">Unggah &amp; Pratinjau</h3>
          <p className="text-xs text-[#5C6066] mt-0.5 max-w-lg">Sistem otomatis mendeteksi <b>Template Resmi DWG OS</b> atau <b>Format Bulanan Legacy</b> (sheet JANUARI–DESEMBER dengan header NO/TANGGAL/NAMA/LOKASI/PAKET/JUMLAH/UANG MASUK/SISA). Import baru dijalankan setelah Anda konfirmasi.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept=".xlsx,.xlsm" onChange={onSelect} className="text-sm" data-testid="imports-file"/>
          <Button onClick={runPreview} disabled={!file || busy} variant="outline" data-testid="btn-preview-import">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : null} Pratinjau
          </Button>
        </div>

        {preview && (
          <div className="mt-5 space-y-4" data-testid="imports-preview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(preview.counts).map(([k,v]) => (
                <div key={k} className="p-3 rounded-lg bg-[#FAF6EE] border border-[#E5E0D8]">
                  <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">{k.replace("_"," ")}</div>
                  <div className="font-mono text-2xl font-bold text-[#1E2022]">{v}</div>
                  <div className="text-[10px] text-[#5C6066]">baris</div>
                </div>
              ))}
            </div>

            {errCount > 0 && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200" data-testid="imports-errors">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-600"/>
                  <div className="font-semibold text-sm text-red-700">{errCount} error — perbaiki dulu sebelum import</div>
                </div>
                <ul className="text-xs text-red-700 space-y-0.5 max-h-40 overflow-y-auto list-disc list-inside">
                  {preview.errors.slice(0, 20).map((e, i) => (
                    <li key={i}><b>{e.sheet}</b> baris {e.row}: {e.msg}</li>
                  ))}
                  {errCount > 20 && <li>... dan {errCount - 20} error lain</li>}
                </ul>
              </div>
            )}

            {warnCount > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600"/>
                  <div className="font-semibold text-sm text-amber-700">{warnCount} peringatan (tetap bisa import, tapi baris ini akan dilewati)</div>
                </div>
                <ul className="text-xs text-amber-700 space-y-0.5 max-h-32 overflow-y-auto list-disc list-inside">
                  {preview.warnings.slice(0, 15).map((w, i) => (
                    <li key={i}><b>{w.sheet}</b> baris {w.row}: {w.msg}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={runExecute} disabled={busy || errCount > 0} className="bg-brand hover:bg-brand/90 text-white" data-testid="btn-execute-import">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <CheckCircle2 className="w-4 h-4 mr-1.5"/>}
                Konfirmasi &amp; Import
              </Button>
            </div>
          </div>
        )}

        {done && (
          <div className="mt-5 p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="imports-result">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600"/>
              <div className="font-semibold text-emerald-700">Import berhasil</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              <SumItem k="Klien Baru" v={done.summary.clients_new || 0}/>
              <SumItem k="Booking Baru" v={done.summary.bookings_new || 0}/>
              <SumItem k="Merged" v={done.summary.bookings_merged || 0}/>
              <SumItem k="Divisi Auto" v={done.summary.services_auto_added || 0}/>
              <SumItem k="Pembayaran" v={done.summary.payments}/>
            </div>
            <div className="text-xs text-emerald-700 mt-2">Cocok berdasarkan Booking Code → WA+tanggal → nama+tanggal. Data existing tidak ditimpa. Paket Master Pricelist otomatis di-sync ke divisi terkait.</div>
          </div>
        )}
      </Card>
    </div>
  );
}

function SumItem({ k, v }) {
  return (
    <div className="p-2 rounded bg-white border border-emerald-200">
      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">{k}</div>
      <div className="font-mono text-lg font-bold text-emerald-800">{v}</div>
    </div>
  );
}
